/**
 * design_handoff_domain_links — one radio band inside the Domain card.
 * Only the selected option renders a body. Locked options (plan gate)
 * replace the radio with a lock glyph, grey the label, and show one gray
 * "X plan or higher" badge + a "See plans" link — never a colored badge,
 * never shown on unlocked options.
 *
 * Radio's own internal label span can't carry this row's richer markup
 * (title + badge + description as separate blocks), so the radio renders
 * dot-only and a second, independent `<label htmlFor>` wraps the text block
 * — both bound to the same input id, both toggle it, per spec's own
 * accessibility note ("each option label is a <label for>").
 */
import React from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import Radio from "../../../theme/untitled/Radio/Radio";
import Badge from "../../../theme/untitled/Badge/Badge";
import Icon from "../../../theme/untitled/icons/Icon";
import { PLAN_NAMES } from "../../../../../consts";
import styles from "./OptionRow.module.scss";

const OptionRow = ({
  type,
  label,
  description,
  isSelected,
  onSelect,
  allowedPlans = [],
  currentPlan,
  children,
}) => {
  const id = `domain-type-${type}`;
  const isLocked = allowedPlans.length > 0 && !allowedPlans.includes(currentPlan);
  const thresholdLabel = isLocked ? `${PLAN_NAMES[allowedPlans[0]]} plan or higher` : null;

  return (
    <div className={classNames(styles.root, { [styles["is-locked"]]: isLocked })}>
      {isLocked ? (
        <Icon name="Lock01" size={16} className={styles.lockIcon} />
      ) : (
        <Radio
          id={id}
          name="domain-type"
          checked={isSelected}
          onChange={onSelect}
          className={styles.radio}
        />
      )}

      <div className={styles.content}>
        {isLocked ? (
          <div className={styles.titleRow}>
            <span className={styles.title}>{label}</span>
            <Badge color="gray" size="sm">
              {thresholdLabel}
            </Badge>
            <Link to="/organization-settings/billing/plans" className={styles.seePlans}>
              See plans
            </Link>
          </div>
        ) : (
          <label htmlFor={id} className={styles.titleRow}>
            <span className={styles.title}>{label}</span>
          </label>
        )}

        <p className={styles.description}>{description}</p>

        {isSelected && !isLocked && <div className={styles.body}>{children}</div>}
      </div>
    </div>
  );
};

export default OptionRow;
